import {
  easeInOut,
  easeOut,
  emphasizedAccelerate,
  emphasizedDecelerate,
  popScale,
  progressAt,
} from 'modules/match-video/domain/scenes/kit';

/**
 * The exit of a set A plate, counted back from the end of its window: the body folds into the tag (.3 s), the text
 * fades (.15 s), then the tag wipes out to the left (.25 s after .18 s) — `.out .x-fold / .x-fade / .x-wipe`.
 */
export const NIGHT_PLATE_EXIT_SEC = 0.43;
const FOLD_SEC = 0.3;
const FADE_SEC = 0.15;
const WIPE_OUT_AT = 0.18;
const WIPE_OUT_SEC = 0.25;

/** Shared by both plates: the tag wipe, the body unfold, the text fade-out. */
export interface PlateFrameState {
  /** 0…1 — how far the tag's wipe has revealed it (`clip-path: inset(...)`). */
  tagReveal: number;
  /** Horizontal scale of the body from its left edge. */
  bodyScaleX: number;
  /** Opacity of the body text on the exit. */
  textAlpha: number;
}

export interface NightGoalPlateState extends PlateFrameState {
  /** Shift of the gloss across the tag, px from its left edge (`sweep`: −260 → 520). */
  glossX: number;
  logoScale: number;
  /** `stroke-dashoffset` of the outlined number, 420 → 0. */
  numberDashOffset: number;
  playerRise: number;
  teamRise: number;
  minute: { dx: number; alpha: number };
}

export interface NightDangerPlateState extends PlateFrameState {
  /** The pulse wave around the tag, `null` between and after its two runs. */
  pulse: { spread: number; alpha: number } | null;
  textRise: number;
}

const dec = (t: number, delaySec: number, durationSec: number) => emphasizedDecelerate(progressAt(t, delaySec, durationSec));

/** `windowSec - (windowSec - exit)` is not exactly `exit` in floating point: the last frame would keep a 1e-15 sliver. */
const snapToZero = (value: number) => (value < 1e-9 ? 0 : value);

/**
 * When the exit starts the entrance classes are gone: every entering element jumps to its final state, as in the
 * mockup where `.out` replaces `.play`. So the entrance is read at `t`, or at its end once the exit has begun.
 */
function frameState(t: number, windowSec: number): PlateFrameState & { entranceT: number } {
  const exitAt = windowSec - NIGHT_PLATE_EXIT_SEC;
  if (t < exitAt) {
    return { entranceT: t, tagReveal: dec(t, 0, 0.3), bodyScaleX: dec(t, 0.15, 0.45), textAlpha: 1 };
  }
  const x = t - exitAt;
  return {
    entranceT: Number.POSITIVE_INFINITY,
    tagReveal: snapToZero(1 - emphasizedAccelerate(progressAt(x, WIPE_OUT_AT, WIPE_OUT_SEC))),
    bodyScaleX: snapToZero(1 - emphasizedAccelerate(progressAt(x, 0, FOLD_SEC))),
    textAlpha: snapToZero(1 - progressAt(x, 0, FADE_SEC)),
  };
}

/** The goal plate of set A at `t` seconds since it appeared, in a window of `windowSec`. */
export function nightGoalPlateState(t: number, windowSec: number): NightGoalPlateState {
  const { entranceT: e, ...frame } = frameState(t, windowSec);
  const minute = dec(e, 0.65, 0.4);
  return {
    ...frame,
    glossX: -260 + 780 * easeInOut(progressAt(e, 0.95, 0.7)),
    logoScale: popScale(e, 0.38, 0.45),
    // `draw`: the dash runs out over the first 80 % (.64 s of .8 s), `ease-out`; the number stays an outline.
    numberDashOffset: 420 * (1 - easeOut(progressAt(e, 0.4, 0.64))),
    playerRise: dec(e, 0.45, 0.6),
    teamRise: dec(e, 0.55, 0.6),
    minute: { dx: 30 * (1 - minute), alpha: minute * frame.textAlpha },
  };
}

const PULSE_AT = 0.45;
const PULSE_SEC = 1;
const PULSE_RUNS = 2;

/** The dangerous-moment plate of set A: the same frame, a pulse twice instead of the gloss. */
export function nightDangerPlateState(t: number, windowSec: number): NightDangerPlateState {
  const { entranceT: e, ...frame } = frameState(t, windowSec);
  const since = e - PULSE_AT;
  const running = since >= 0 && since < PULSE_SEC * PULSE_RUNS;
  const k = running ? easeOut((since % PULSE_SEC) / PULSE_SEC) : 0;
  return {
    ...frame,
    // `pulse`: box-shadow 0 → 44 px spread, orange .75 → 0.
    pulse: running ? { spread: 44 * k, alpha: 0.75 * (1 - k) } : null,
    textRise: dec(e, 0.4, 0.6),
  };
}
